import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Select, Card, Space, Typography, Tag, Spin, Empty, DatePicker,
  Tooltip, Button, Dropdown, Checkbox, Modal, Badge
} from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart, Brush
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { dataInsightApi } from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ---- 指标定义（3维度 × 5指标 = 15项预设）----
const METRIC_DIMENSIONS = [
  {
    key: 'document',
    label: '单据维度',
    metrics: [
      { key: 'document_no_risk_precision', label: '无风险精确率' },
      { key: 'document_no_risk_recall', label: '无风险召回率' },
      { key: 'document_risk_precision', label: '有风险精确率' },
      { key: 'document_risk_recall', label: '有风险召回率' },
      { key: 'document_accuracy', label: '准确率' },
    ],
  },
  {
    key: 'rule_avg',
    label: '规则平均维度',
    metrics: [
      { key: 'rule_avg_no_risk_precision', label: '无风险精确率' },
      { key: 'rule_avg_no_risk_recall', label: '无风险召回率' },
      { key: 'rule_avg_risk_precision', label: '有风险精确率' },
      { key: 'rule_avg_risk_recall', label: '有风险召回率' },
      { key: 'rule_avg_accuracy', label: '准确率' },
    ],
  },
  {
    key: 'rule_detail',
    label: '规则明细维度',
    metrics: [
      { key: 'rule_detail_no_risk_precision', label: '无风险精确率' },
      { key: 'rule_detail_no_risk_recall', label: '无风险召回率' },
      { key: 'rule_detail_risk_precision', label: '有风险精确率' },
      { key: 'rule_detail_risk_recall', label: '有风险召回率' },
      { key: 'rule_detail_accuracy', label: '准确率' },
    ],
  },
  {
    key: 'feature_extraction',
    label: '要素抽取维度',
    metrics: [
      { key: 'feature_extraction_file_type_accuracy', label: '文件类型识别准确率' },
      { key: 'feature_extraction_element_accuracy', label: '要素抽取准确率' },
    ],
  },
];

// 默认选中的指标：单据维度全部5项 + 要素抽取维度2项
const DEFAULT_METRICS = [
  'document_no_risk_precision',
  'document_no_risk_recall',
  'document_risk_precision',
  'document_risk_recall',
  'document_accuracy',
  'feature_extraction_file_type_accuracy',
  'feature_extraction_element_accuracy',
];

// 评估类型定义
const CATEGORY_OPTIONS = [
  { value: 'BASELINE', label: '基线评估', color: '#4F8EF7' },
  { value: 'ACCEPTANCE', label: '验收评估', color: '#FA8C16' },
  { value: 'PERIODIC', label: '线上评估', color: '#52C41A' },
];

const CATEGORY_COLOR_MAP = {
  BASELINE: { color: '#4F8EF7', label: '基线' },
  ACCEPTANCE: { color: '#FA8C16', label: '评估验收' },
  PERIODIC: { color: '#52C41A', label: '线上周期' },
};

// 图表颜色池（用于不同指标）
const LINE_COLORS = [
  '#4F8EF7', '#FF6B6B', '#52C41A', '#FAAD14', '#722ED1',
  '#13C2C2', '#EB2F96', '#FA8C16', '#A0D911', '#1890FF',
  '#F5222D', '#52C41A', '#FADB14', '#2F54EB', '#722ED1',
];

// ---- 全局指标选择下拉组件 ----
function GlobalMetricSelector({ selectedMetrics, onChange }) {
  const allMetricKeys = METRIC_DIMENSIONS.flatMap(d => d.metrics.map(m => m.key));

  const menu = (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        padding: '12px 0',
        minWidth: 280,
        maxHeight: 400,
        overflowY: 'auto',
      }}
    >
      {METRIC_DIMENSIONS.map(dim => (
        <div key={dim.key} style={{ padding: '4px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: '#888', marginBottom: 4, marginTop: 4 }}>
            {dim.label}
          </div>
          {dim.metrics.map(m => (
            <div
              key={m.key}
              style={{ padding: '4px 8px', cursor: 'pointer', borderRadius: 4 }}
              onClick={() => {
                const next = selectedMetrics.includes(m.key)
                  ? selectedMetrics.filter(k => k !== m.key)
                  : [...selectedMetrics, m.key];
                if (next.length > 0) onChange(next);
              }}
            >
              <Checkbox checked={selectedMetrics.includes(m.key)} style={{ pointerEvents: 'none' }}>
                <span style={{ fontSize: 13 }}>{m.label}</span>
              </Checkbox>
            </div>
          ))}
        </div>
      ))}
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 16px', marginTop: 4, display: 'flex', gap: 8 }}>
        <Button size="small" onClick={() => onChange(DEFAULT_METRICS)}>重置</Button>
        <Button size="small" type="primary" onClick={() => onChange(allMetricKeys)}>全选</Button>
      </div>
    </div>
  );

  return (
    <Dropdown overlay={menu} trigger={['click']} placement="bottomLeft">
      <Button icon={<FilterOutlined />} size="small">
        指标选择 ({selectedMetrics.length})
      </Button>
    </Dropdown>
  );
}

// ---- 效果水位趋势图（左图）----
function WatermarkChart({ scenarioId, scenarioName, selectedCategories, dateRange, selectedMetrics, chartData, loading, activePoint, setActivePoint }) {
  const navigate = useNavigate();
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [selectedCustomMetrics, setSelectedCustomMetrics] = useState([]);

  // 从 chartData 中动态提取自定义维度选项（来自 metrics.customMetrics 的 key）
  const customMetricOptions = useMemo(() => {
    const dimensionSet = new Set();
    (chartData || []).forEach(d => {
      const customMetrics = d.metrics?.customMetrics;
      if (customMetrics && typeof customMetrics === 'object') {
        Object.keys(customMetrics).forEach(dimName => dimensionSet.add(dimName));
      }
    });
    return Array.from(dimensionSet).map(name => ({ value: name, text: name }));
  }, [chartData]);

  // 当 customMetricOptions 变化时，清理掉已不存在的选中项
  useEffect(() => {
    const validKeys = new Set(customMetricOptions.map(m => m.value));
    setSelectedCustomMetrics(prev => prev.filter(k => validKeys.has(k)));
  }, [customMetricOptions]);

  const allMetricMap = {};
  METRIC_DIMENSIONS.forEach(dim => dim.metrics.forEach(m => {
    allMetricMap[m.key] = `${dim.label} · ${m.label}`;
  }));
  customMetricOptions.forEach(m => { allMetricMap[m.value] = `自定义 · ${m.text}`; });

  const allSelectedMetrics = useMemo(
    () => [...selectedMetrics, ...selectedCustomMetrics],
    [selectedMetrics, selectedCustomMetrics]
  );

  // 适配后端数据结构：benchmarkType -> setCategory, benchmarkVersion -> benchmarkName
  const normalizedData = useMemo(() =>
    (chartData || []).map(d => ({
      ...d,
      setCategory: d.benchmarkType || d.setCategory,
      benchmarkName: d.benchmarkVersion || d.benchmarkName,
    })),
    [chartData]
  );

  const filteredData = useMemo(() =>
    normalizedData.filter(d => selectedCategories.includes(d.setCategory)),
    [normalizedData, selectedCategories]
  );

  // 按日期合并数据：同一天不同评估类型的指标用 "metricKey_CATEGORY" 作为独立字段
  // 确保同一日期在x轴只占一个位置，不同评估类型用不同颜色的点/线展示
  const processedChartData = useMemo(() => {
    const dateMap = new Map();
    filteredData.forEach(d => {
      const dateKey = d.date;
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { date: dateKey, _rawByCategory: {} });
      }
      const row = dateMap.get(dateKey);
      const category = d.setCategory;
      row._rawByCategory[category] = d;

      const metrics = d.metrics || {};
      const presetMetrics = metrics.presetMetrics || {};

      // 预设指标：按 "metricKey_CATEGORY" 存入合并行
      selectedMetrics.forEach(key => {
        const categoryKey = `${key}_${category}`;

        // 要素抽取维度：从 featureExtractionMetrics 中取值
        if (key === 'feature_extraction_file_type_accuracy') {
          const feMetrics = metrics.featureExtractionMetrics || {};
          const rawValue = feMetrics.file_type_accuracy;
          row[categoryKey] = rawValue !== undefined && rawValue !== null ? +rawValue : null;
          return;
        }
        if (key === 'feature_extraction_element_accuracy') {
          const feMetrics = metrics.featureExtractionMetrics || {};
          const rawValue = feMetrics.element_accuracy;
          row[categoryKey] = rawValue !== undefined && rawValue !== null ? +rawValue : null;
          return;
        }

        let dimensionKey, shortKey;
        if (key.startsWith('rule_avg_')) {
          dimensionKey = 'rule_avg';
          shortKey = key.replace(/^rule_avg_/, '');
        } else if (key.startsWith('rule_detail_')) {
          dimensionKey = 'rule_detail';
          shortKey = key.replace(/^rule_detail_/, '');
        } else {
          dimensionKey = 'document';
          shortKey = key.replace(/^document_/, '');
        }
        const dimensionData = presetMetrics[dimensionKey] || {};
        const valueKey = `${shortKey}_value`;
        const rawValue = dimensionData[valueKey];
        row[categoryKey] = rawValue !== undefined && rawValue !== null ? +(rawValue * 100).toFixed(2) : null;
      });

      // 自定义指标：按 "dimName_CATEGORY" 存入合并行
      const customMetricsData = metrics.customMetrics || {};
      selectedCustomMetrics.forEach(dimName => {
        const dimData = customMetricsData[dimName];
        const categoryKey = `${dimName}_${category}`;
        if (dimData) {
          const rawValue = dimData.accuracy_value;
          row[categoryKey] = rawValue !== undefined && rawValue !== null ? +(rawValue * 100).toFixed(2) : null;
        } else {
          row[categoryKey] = null;
        }
      });
    });

    // 过滤掉所有指标值均为null的日期条目（避免无数据日期占据X轴位置）
    return Array.from(dateMap.values())
      .filter(row => {
        // 检查该行是否有至少一个非null的指标值
        return Object.keys(row).some(key => 
          key !== 'date' && key !== '_rawByCategory' && row[key] !== null && row[key] !== undefined
        );
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredData, selectedMetrics, selectedCustomMetrics]);

  const handleChartClick = useCallback((chartEvent) => {
    console.log('[DataInsight] chart click event:', chartEvent);
    if (chartEvent && chartEvent.activePayload && chartEvent.activePayload.length > 0) {
      const payload = chartEvent.activePayload[0]?.payload;
      console.log('[DataInsight] click payload:', payload);
      // 从 _rawByCategory 中取第一个有数据的分类作为默认选中
      if (payload?._rawByCategory) {
        const categories = Object.keys(payload._rawByCategory);
        console.log('[DataInsight] available categories:', categories);
        // 优先使用点击的Line对应的分类
        const clickedDataKey = chartEvent.activePayload[0]?.dataKey;
        let clickedCategory = null;
        if (clickedDataKey) {
          // dataKey 格式为 "metricKey_CATEGORY"
          const parts = clickedDataKey.split('_');
          clickedCategory = parts[parts.length - 1];
        }
        console.log('[DataInsight] clickedDataKey:', clickedDataKey, 'clickedCategory:', clickedCategory);
        const targetCategory = clickedCategory && payload._rawByCategory[clickedCategory]
          ? clickedCategory
          : categories[0];
        if (targetCategory) {
          const targetData = payload._rawByCategory[targetCategory];
          console.log('[DataInsight] set activePoint:', targetData);
          setActivePoint(targetData);
        }
      } else {
        console.warn('[DataInsight] payload missing _rawByCategory');
      }
    } else if (chartEvent && chartEvent.activeLabel) {
      // 回退：activePayload 为空时，通过 activeLabel（日期）直接查找数据
      const dateRow = processedChartData.find(row => row.date === chartEvent.activeLabel);
      console.log('[DataInsight] fallback by activeLabel:', chartEvent.activeLabel, 'row:', dateRow);
      if (dateRow?._rawByCategory) {
        const categories = Object.keys(dateRow._rawByCategory);
        const targetCategory = categories[0];
        if (targetCategory) {
          console.log('[DataInsight] set activePoint (fallback):', dateRow._rawByCategory[targetCategory]);
          setActivePoint(dateRow._rawByCategory[targetCategory]);
        }
      } else {
        console.warn('[DataInsight] fallback row missing _rawByCategory');
      }
    } else {
      console.warn('[DataInsight] activePayload empty, click ignored');
    }
  }, [processedChartData]);

  const makeDot = (color, category) => (props) => {
    const { cx, cy, payload, value } = props;
    // 如果没有指标结果（value 为 null/undefined），不渲染点
    if (value === null || value === undefined) return null;
    const catColor = CATEGORY_COLOR_MAP[category]?.color || color;
    const raw = payload?._rawByCategory?.[category];
    const isActive = raw && activePoint && raw.benchmarkId === activePoint.benchmarkId;

    const handleDotClick = (e) => {
      e.stopPropagation();
      if (raw) {
        console.log('[DataInsight] dot clicked, category:', category, 'raw:', raw);
        setActivePoint(raw);
      } else {
        console.warn('[DataInsight] dot clicked but no raw data for category:', category);
      }
    };

    return (
      <circle
        key={`dot-${cx}-${cy}-${category}`}
        cx={cx} cy={cy}
        r={isActive ? 7 : 4}
        fill={catColor}
        stroke={isActive ? '#333' : '#fff'}
        strokeWidth={isActive ? 2 : 1.5}
        style={{ cursor: 'pointer' }}
        onClick={handleDotClick}
      />
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const rawData = payload[0]?.payload;
    // 按分类分组展示指标值
    const categoryDataMap = {};
    payload.forEach(p => {
      // dataKey 格式: "metricKey_CATEGORY"
      const dataKey = p.dataKey;
      const lastUnderscoreIdx = dataKey.lastIndexOf('_');
      // 检查后缀是否为已知分类
      const possibleCategory = dataKey.substring(lastUnderscoreIdx + 1);
      const metricName = dataKey.substring(0, lastUnderscoreIdx);
      const category = CATEGORY_COLOR_MAP[possibleCategory] ? possibleCategory : null;
      if (category) {
        if (!categoryDataMap[category]) categoryDataMap[category] = [];
        categoryDataMap[category].push({
          metricKey: metricName,
          label: allMetricMap[metricName] || metricName,
          value: p.value,
          color: CATEGORY_COLOR_MAP[category].color,
        });
      }
    });

    return (
      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8,
        padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxWidth: 320,
        pointerEvents: 'none',
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: '#333', fontSize: 12 }}>{label}</div>
        {Object.entries(categoryDataMap).map(([category, metrics]) => {
          const raw = rawData?._rawByCategory?.[category];
          return (
            <div key={category} style={{ marginBottom: 6, paddingBottom: 4, borderBottom: '1px dashed #f0f0f0' }}>
              <div style={{ marginBottom: 3 }}>
                <Tag color={CATEGORY_COLOR_MAP[category]?.color} style={{ fontSize: 10 }}>
                  {CATEGORY_COLOR_MAP[category]?.label}
                </Tag>
                {raw?.benchmarkName && <span style={{ fontSize: 11, color: '#888' }}>{raw.benchmarkName}</span>}
              </div>
              {metrics.map(m => (
                <div key={m.metricKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, marginBottom: 1 }}>
                  <span style={{ color: m.color }}>{m.label}</span>
                  <span style={{ fontWeight: 600 }}>{m.value !== null && m.value !== undefined ? `${m.value}%` : '-'}</span>
                </div>
              ))}
            </div>
          );
        })}
        <div style={{ marginTop: 4, fontSize: 10, color: '#999' }}>点击数据点查看 Benchmark 详情</div>
      </div>
    );
  };

  return (
    <Card
      size="small"
      style={{ flex: 1, minWidth: 0 }}
      bodyStyle={{ padding: '12px 16px' }}
      title={null}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <LineChartOutlined style={{ color: '#4F8EF7' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>效果水位趋势</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {selectedCategories.map(cat => (
              <Tag key={cat} color={CATEGORY_COLOR_MAP[cat]?.color} style={{ fontSize: 11, margin: 0 }}>
                {CATEGORY_COLOR_MAP[cat]?.label}
              </Tag>
            ))}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Badge count={selectedCustomMetrics.length} size="small" offset={[-4, 4]}>
              <Button
                size="small"
                style={{
                  borderColor: '#ff4d4f',
                  color: '#ff4d4f',
                  fontSize: 12,
                }}
                onClick={() => setCustomModalOpen(true)}
              >
                自定义指标
              </Button>
            </Badge>
          </div>
        </div>
      </div>

      <Modal
        title="自定义指标选择"
        open={customModalOpen}
        onCancel={() => setCustomModalOpen(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#888' }}>已选 {selectedCustomMetrics.length} / {customMetricOptions.length} 项</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" onClick={() => setSelectedCustomMetrics([])}>清空</Button>
              <Button size="small" type="primary" onClick={() => setCustomModalOpen(false)}>确定</Button>
            </div>
          </div>
        }
        width={480}
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
          以下为自定义扩展指标，默认不启用，勾选后加入图表展示
        </div>
        {customMetricOptions.map(m => (
          <div
            key={m.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 4,
              background: selectedCustomMetrics.includes(m.value) ? '#f0f7ff' : '#fafafa',
              border: `1px solid ${selectedCustomMetrics.includes(m.value) ? '#91caff' : '#f0f0f0'}`,
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelectedCustomMetrics(prev =>
                prev.includes(m.value)
                  ? prev.filter(k => k !== m.value)
                  : [...prev, m.value]
              );
            }}
          >
            <div>
              <Checkbox
                checked={selectedCustomMetrics.includes(m.value)}
                style={{ marginRight: 8, pointerEvents: 'none' }}
              />
              <span style={{ fontSize: 13, fontWeight: 500 }}>{m.text}</span>
            </div>
            <span style={{ fontSize: 11, color: '#aaa' }}>{m.value}</span>
          </div>
        ))}
      </Modal>

      <Spin spinning={!!loading}>
        {processedChartData.length === 0 ? (
          loading ? null : <Empty description="暂无数据" style={{ padding: '40px 0' }} />
        ) : (() => {
          // 每个数据点至少 80px 宽，数据点少时图表不会撑满全宽导致点过度分散
          const pointWidth = 80;
          const minWidth = 500;
          const needScroll = processedChartData.length * pointWidth > minWidth;
          const chartPx = needScroll ? processedChartData.length * pointWidth : '100%';
          return (
            <div style={{ overflowX: needScroll ? 'auto' : 'hidden' }}>
              <div style={{ width: chartPx, minWidth: minWidth }}>
                <ResponsiveContainer width="100%" height={440}>
                  <LineChart
                    data={processedChartData}
                    margin={{ top: 10, right: 60, left: 0, bottom: 0 }}
                    onClick={handleChartClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      angle={-30}
                      textAnchor="end"
                      height={50}
                      padding={{ left: 40, right: 40 }}
                    />
                    <YAxis
                      tickFormatter={v => `${v}%`}
                      domain={[0, 100]}
                      tick={{ fontSize: 11 }}
                      width={48}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend
                      formatter={key => {
                        // dataKey 格式: "metricKey_CATEGORY"
                        const lastUnderscoreIdx = key.lastIndexOf('_');
                        const possibleCategory = key.substring(lastUnderscoreIdx + 1);
                        if (CATEGORY_COLOR_MAP[possibleCategory]) {
                          return CATEGORY_COLOR_MAP[possibleCategory].label;
                        }
                        return allMetricMap[key] || key;
                      }}
                      wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                    />
                    {selectedCategories.map(category => {
                      const catColor = CATEGORY_COLOR_MAP[category]?.color || '#999';
                      let categoryLegendShown = false;
                      return allSelectedMetrics.map((key) => {
                        const categoryKey = `${key}_${category}`;
                        // 只渲染有实际数据的 Line，避免无数据时也出现 legend
                        const hasData = processedChartData.some(row => row[categoryKey] !== null && row[categoryKey] !== undefined);
                        if (!hasData) return null;
                        // 每个 category 只显示一个 legend 条目，避免重复
                        const showLegend = !categoryLegendShown;
                        categoryLegendShown = true;
                        return (
                          <Line
                            key={categoryKey}
                            type="monotone"
                            dataKey={categoryKey}
                            stroke={catColor}
                            strokeWidth={2}
                            strokeDasharray={selectedCustomMetrics.includes(key) ? '6 3' : undefined}
                            dot={makeDot(catColor, category)}
                            activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                            connectNulls={false}
                            legendType={showLegend ? 'circle' : 'none'}
                          />
                        );
                      });
                    })}
                    {processedChartData.length > 30 && (
                      <Brush
                        dataKey="date"
                        height={30}
                        stroke="#4F8EF7"
                        tickFormatter={v => v}
                        startIndex={0}
                        endIndex={Math.min(30, processedChartData.length - 1)}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })()}
      </Spin>

      {activePoint && (() => {
        // 根据 activePoint 的日期查找当天所有类别的 benchmark 数据
        const dateRow = processedChartData.find(row => row.date === activePoint.date);
        const allCategories = dateRow?._rawByCategory || {};
        const categoryEntries = Object.entries(allCategories);
        return (
          <div style={{
            background: '#f0f7ff',
            border: '1px solid #91caff',
            borderRadius: 6,
            padding: '8px 12px',
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1677FF', marginBottom: 2 }}>
              {activePoint.date} 共 {categoryEntries.length} 个 Benchmark
            </div>
            {categoryEntries.map(([category, data]) => (
              <div
                key={category}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: '4px 8px',
                  borderRadius: 4,
                  background: activePoint.setCategory === category ? '#e6f4ff' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag color={CATEGORY_COLOR_MAP[category]?.color} style={{ margin: 0 }}>
                    {CATEGORY_COLOR_MAP[category]?.label}
                  </Tag>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{data.benchmarkName}</span>
                </div>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => window.open(`#/benchmark/${data.benchmarkId}`, '_blank')}
                >
                  查看详情
                </Button>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setActivePoint(null)}>关闭</Button>
            </div>
          </div>
        );
      })()}
    </Card>
  );
}

// ---- 样本使用情况图（右图）----
function SampleUsageChart({ scenarioId, selectedCategories, monthRange, chartData: rawChartData, loading }) {

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const totalItem = payload.find(p => p.dataKey === 'totalSamples');
    const usedItem = payload.find(p => p.dataKey === 'usedSamples');
    const rateItem = payload.find(p => p.dataKey === 'usageRate');
    const raw = payload[0]?.payload?._raw;
    return (
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
        {raw?.setCategory && (
          <div style={{ marginBottom: 5 }}>
            <Tag color={CATEGORY_COLOR_MAP[raw.setCategory]?.color} style={{ fontSize: 10 }}>
              {CATEGORY_COLOR_MAP[raw.setCategory]?.label}
            </Tag>
          </div>
        )}
        <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {totalItem && <div style={{ color: '#4F8EF7' }}>样本总数：{totalItem.value?.toLocaleString() || '-'}</div>}
          {usedItem && <div style={{ color: '#52C41A' }}>已执行样本：{usedItem.value?.toLocaleString() || '-'}</div>}
          {rateItem && <div style={{ color: '#FA8C16', fontWeight: 600 }}>样本使用率：{rateItem.value}%</div>}
        </div>
      </div>
    );
  };

  const processedChartData = useMemo(() => {
    const monthSet = new Set();
    (rawChartData || []).forEach(d => monthSet.add(d.month));
    const months = Array.from(monthSet).sort();

    return months.map(month => {
      const monthItems = (rawChartData || []).filter(d => d.month === month);
      const filtered = monthItems.filter(d => {
        if (d.byCategory) {
          return true;
        }
        return true;
      });

      let totalSamples = 0;
      let usedSamples = 0;

      filtered.forEach(d => {
        if (d.byCategory) {
          selectedCategories.forEach(cat => {
            const catData = d.byCategory?.[cat];
            if (catData) {
              totalSamples += catData.totalSamples || 0;
              usedSamples += catData.usedSamples || 0;
            }
          });
        } else {
          totalSamples += d.totalSamples || 0;
          usedSamples += d.usedSamples || 0;
        }
      });

      const usageRate = totalSamples > 0 ? +(usedSamples / totalSamples * 100).toFixed(1) : 0;
      return { month, totalSamples, usedSamples, usageRate };
    });
  }, [rawChartData, selectedCategories]);

  return (
    <Card
      size="small"
      style={{ width: 420, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ padding: '12px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}
      title={null}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <BarChartOutlined style={{ color: '#52C41A' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>评测样本使用</span>
        </div>
      </div>
      <Spin spinning={!!loading} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {processedChartData.length === 0 ? (
          loading ? null : <Empty description="暂无数据" style={{ padding: '40px 0' }} />
        ) : (
          <ResponsiveContainer width="100%" height={440}>
            <ComposedChart data={processedChartData} margin={{ top: 10, right: 50, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={50} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={50} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v}%`} domain={[0, 100]} tick={{ fontSize: 11 }} width={44} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar yAxisId="left" dataKey="totalSamples" name="样本总数" fill="#E8F0FE" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="left" dataKey="usedSamples" name="已执行样本" fill="#4F8EF7" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="usageRate" name="使用率%" stroke="#FA8C16" strokeWidth={2.5} dot={{ r: 4, fill: '#FA8C16' }} activeDot={{ r: 6 }} />
              {processedChartData.length > 6 && (
                <Brush
                  dataKey="month"
                  height={30}
                  stroke="#52C41A"
                  tickFormatter={v => v}
                  startIndex={0}
                  endIndex={Math.min(5, processedChartData.length - 1)}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Spin>
    </Card>
  );
}

// ---- 单个场景卡片 ----
function ScenarioCard({ scenario, selectedCategories, dateRange, monthRange, selectedMetrics, filterDataMap, filterLoading }) {
  const typeLabel = scenario.type === 'FEATURE_EXTRACTION' ? '要素抽取' : '机审场景';
  const typeColor = scenario.type === 'FEATURE_EXTRACTION' ? 'green' : 'blue';
  
  // 从父组件传递的数据映射中获取当前场景的数据
  const scenarioFilterData = filterDataMap[scenario.id] || { watermarkTrend: [], sampleUsage: [], loading: false };
  const [activePoint, setActivePoint] = useState(null);

  return (
    <Card
      style={{ marginBottom: 20, borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
      bodyStyle={{ padding: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Title level={5} style={{ margin: 0 }}>{scenario.name}</Title>
        <Tag color={typeColor}>{typeLabel}</Tag>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <WatermarkChart
          scenarioId={scenario.id}
          scenarioName={scenario.name}
          selectedCategories={selectedCategories}
          dateRange={dateRange}
          selectedMetrics={selectedMetrics}
          chartData={scenarioFilterData.watermarkTrend}
          loading={filterLoading}
          activePoint={activePoint}
          setActivePoint={setActivePoint}
        />
        <SampleUsageChart
          scenarioId={scenario.id}
          selectedCategories={selectedCategories}
          monthRange={monthRange}
          chartData={scenarioFilterData.sampleUsage}
          loading={filterLoading}
        />
      </div>
    </Card>
  );
}

// ---- 主页面 ----
export default function DataInsight() {
  const [domains, setDomains] = useState([]);
  const [selectedDomainIds, setSelectedDomainIds] = useState([]);
  const [allScenarios, setAllScenarios] = useState([]);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);


  const [selectedCategories, setSelectedCategories] = useState(['BASELINE', 'ACCEPTANCE', 'PERIODIC']);
  const [globalDateRange, setGlobalDateRange] = useState([
    dayjs().subtract(90, 'day'),
    dayjs(),
  ]);
  const [globalMonthRange, setGlobalMonthRange] = useState([
    dayjs().subtract(5, 'month').startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [globalSelectedMetrics, setGlobalSelectedMetrics] = useState(DEFAULT_METRICS);

  // 加载有权限的项目空间列表
  useEffect(() => {
    dataInsightApi.getAuthorizedDomains().then(res => {
      const list = res || [];
      setDomains(list);
      // 默认全选所有项目空间
      if (list.length > 0) {
        setSelectedDomainIds(list.map(d => d.value));
      }
    });
  }, []);

  // 加载机审业务场景列表
  useEffect(() => {
    if (selectedDomainIds.length === 0) {
      setAllScenarios([]);
      setSelectedScenarioIds([]);
      return;
    }
    setLoadingScenarios(true);
    setSelectedScenarioIds([]);

    dataInsightApi.getMachineAuditScenarios(selectedDomainIds).then(res => {
      const scenarios = res || [];
      // 转换为 {id, name, type} 格式
      const allScenariosList = scenarios.map(s => ({
        id: s.value,
        name: s.text,
        type: s.type || 'MACHINE_AUDIT'
      }));
      setAllScenarios(allScenariosList);
      setSelectedScenarioIds(allScenariosList.map(s => s.id));
    }).finally(() => setLoadingScenarios(false));
  }, [selectedDomainIds]);


  const selectedScenarios = useMemo(
    () => allScenarios.filter(s => selectedScenarioIds.includes(s.id)),
    [allScenarios, selectedScenarioIds]
  );

  // 统一管理 filterData 请求，避免子组件重复调用
  const [filterDataMap, setFilterDataMap] = useState({});
  const [filterLoading, setFilterLoading] = useState(false);

  // 将 selectedScenarioIds 序列化为字符串，避免数组引用变化导致重复触发
  const scenarioIdsKey = useMemo(
    () => selectedScenarioIds.slice().sort().join(','),
    [selectedScenarioIds]
  );
  const domainIdsKey = useMemo(
    () => selectedDomainIds.slice().sort().join(','),
    [selectedDomainIds]
  );
  const categoriesKey = useMemo(
    () => selectedCategories.slice().sort().join(','),
    [selectedCategories]
  );
  const metricsKey = useMemo(
    () => globalSelectedMetrics.slice().sort().join(','),
    [globalSelectedMetrics]
  );
  const dateRangeKey = useMemo(
    () => `${globalDateRange[0]?.format('YYYY-MM-DD')}~${globalDateRange[1]?.format('YYYY-MM-DD')}`,
    [globalDateRange]
  );
  const monthRangeKey = useMemo(
    () => `${globalMonthRange[0]?.format('YYYY-MM')}~${globalMonthRange[1]?.format('YYYY-MM')}`,
    [globalMonthRange]
  );

  useEffect(() => {
    if (!scenarioIdsKey) {
      setFilterDataMap({});
      return;
    }

    const scenarioIds = scenarioIdsKey.split(',').map(Number);
    const domainIds = domainIdsKey ? domainIdsKey.split(',').map(Number) : [];

    let cancelled = false;
    setFilterLoading(true);

    const fetchData = async () => {
      const promises = scenarioIds.map(async (scenarioId) => {
        try {
          const res = await dataInsightApi.filterData({
            domainIds,
            scenarioIds: [scenarioId],
            evalTypes: selectedCategories,
            watermarkDateRange: [
              globalDateRange[0]?.format('YYYY-MM-DD'),
              globalDateRange[1]?.format('YYYY-MM-DD')
            ],
            sampleDateRange: [
              globalMonthRange[0]?.format('YYYY-MM'),
              globalMonthRange[1]?.format('YYYY-MM')
            ],
            metricCodes: globalSelectedMetrics,
            customMetricCodes: []
          });
          return {
            scenarioId,
            watermarkTrend: res.watermarkTrend || [],
            sampleUsage: res.sampleUsage || [],
          };
        } catch (error) {
          console.error(`获取场景 ${scenarioId} 数据失败:`, error);
          return { scenarioId, watermarkTrend: [], sampleUsage: [] };
        }
      });

      const results = await Promise.all(promises);
      if (cancelled) return;

      const newDataMap = {};
      results.forEach(r => { newDataMap[r.scenarioId] = r; });
      setFilterDataMap(newDataMap);
      setFilterLoading(false);
    };

    fetchData();
    return () => { cancelled = true; };
  }, [scenarioIdsKey, domainIdsKey, categoriesKey, dateRangeKey, monthRangeKey, metricsKey]);

  return (
    <div style={{ padding: '20px 24px', minHeight: '100vh', background: '#f5f7fa' }}>
      {/* 顶部标题 + 全局筛选 */}
      <div style={{
        background: '#fff',
        borderRadius: 10,
        padding: '16px 20px',
        marginBottom: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* 第一行：标题 + 项目空间 + 业务场景 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg,#4F8EF7,#7B61FF)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LineChartOutlined style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>评测数据洞察</div>
              <div style={{ fontSize: 12, color: '#999' }}>多场景效果水位趋势 & 样本使用分析</div>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Space>
              <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>项目空间</Text>
              <Select
                mode="multiple"
                style={{ minWidth: 200 }}
                value={selectedDomainIds}
                onChange={v => v.length > 0 && setSelectedDomainIds(v)}
                options={domains.map(d => ({ value: d.value, label: d.text }))}
                placeholder="选择项目空间（可多选）"
                maxTagCount={2}
              />
            </Space>
            <Space>
              <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>业务场景</Text>
              <Select
                mode="multiple"
                style={{ minWidth: 240 }}
                value={selectedScenarioIds}
                onChange={setSelectedScenarioIds}
                options={allScenarios.map(s => ({ value: s.id, label: s.name }))}
                placeholder="选择业务场景（可多选）"
                loading={loadingScenarios}
                maxTagCount={2}
              />
            </Space>
            {selectedScenarioIds.length !== allScenarios.length && allScenarios.length > 0 && (
              <Button size="small" type="link" onClick={() => setSelectedScenarioIds(allScenarios.map(s => s.id))}>
                全选
              </Button>
            )}
          </div>
        </div>

        {/* 第二行：评估类型 + 时间筛选 + 指标选择 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          <Space>
            <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>评估类型</Text>
            <Select
              mode="multiple"
              style={{ minWidth: 200 }}
              value={selectedCategories}
              onChange={v => v.length > 0 && setSelectedCategories(v)}
              options={CATEGORY_OPTIONS.map(c => ({ value: c.value, label: c.label }))}
              placeholder="选择评估类型"
              maxTagCount={3}
            />
          </Space>

          <Space>
            <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>时间范围(水位)</Text>
            <RangePicker
              size="small"
              value={globalDateRange}
              onChange={v => v && setGlobalDateRange(v)}
              format="YYYY-MM-DD"
              allowClear={false}
              style={{ width: 220 }}
            />
          </Space>

          <Space>
            <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>时间范围(样本)</Text>
            <RangePicker
              size="small"
              picker="month"
              value={globalMonthRange}
              onChange={v => v && setGlobalMonthRange(v)}
              format="YYYY-MM"
              allowClear={false}
              style={{ width: 180 }}
            />
          </Space>

          <GlobalMetricSelector
            selectedMetrics={globalSelectedMetrics}
            onChange={setGlobalSelectedMetrics}
          />
        </div>

        {/* 提示 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
          <InfoCircleOutlined style={{ color: '#bbb', fontSize: 13 }} />
          <Text style={{ fontSize: 12, color: '#999' }}>
            已选 {selectedDomainIds.length} 个空间、{selectedScenarios.length} 个场景，筛选条件全局生效
          </Text>
        </div>
      </div>

      {/* 场景卡片区域 */}
      {selectedScenarios.length === 0 ? (
        <Card style={{ borderRadius: 10 }}>
          <Empty
            description={selectedDomainIds.length === 0 ? '请先选择项目空间' : '请在顶部选择至少一个业务场景'}
            style={{ padding: '60px 0' }}
          />
        </Card>
      ) : (
        selectedScenarios.map(scenario => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            selectedCategories={selectedCategories}
            dateRange={globalDateRange}
            monthRange={globalMonthRange}
            selectedMetrics={globalSelectedMetrics}
            filterDataMap={filterDataMap}
            filterLoading={filterLoading}
          />
        ))
      )}
    </div>
  );
}
